import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import {
  createTrueOrFalseQuestionIntoActivity,
  updateTrueOrFalseQuestionIntoActivity,
} from '@/data/services/question/trueOrFalseQuestionServices';
import Activity from '@/models/Activity';
import { TrueOrFalseQuestion } from '@/models/Question';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import alert from '@/utils/shared/alert';
import TrueOrFalseForm, { TrueOrFalseFormFields } from './TrueOrFalseForm';

type TrueOrFalseFormServiceProps = {
  activity: Activity;
  question: TrueOrFalseQuestion;
  isActive?: boolean;
};

export default function TrueOrFalseFormService({
  activity,
  question,
  isActive,
}: TrueOrFalseFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const { ensureEditable, resolveEditable } = useEditableQuestion(activity);

  const handleCreate = async (
    data: TrueOrFalseFormFields,
  ): Promise<TrueOrFalseQuestion> => {
    const { maxGrade, feedbacks, ...rest } = data;
    const editableActivity = await ensureEditable();
    const saved = await createTrueOrFalseQuestionIntoActivity(
      editableActivity.id,
      {
        type: question.type,
        identifier: question.identifier,
        order: question.order,
        feedbacks: sanitizeFeedbacks(feedbacks),
        ...rest,
      },
    );
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: TrueOrFalseFormFields,
  ): Promise<TrueOrFalseQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<TrueOrFalseFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      isTrue: question.isTrue ?? true,
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const changedFields: Partial<Omit<TrueOrFalseFormFields, 'maxGrade'>> = {};
    if (!isEqual(oldValues.title, rest.title)) {
      changedFields.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      changedFields.context = rest.context;
    }
    if (oldValues.isTrue !== rest.isTrue) {
      changedFields.isTrue = rest.isTrue;
    }
    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      changedFields.feedbacks = newSanitizedFeedbacks;
    }

    const { updateTarget } = await resolveEditable(question);
    const saved = await updateTarget(
      changedFields,
      updateTrueOrFalseQuestionIntoActivity,
    );

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: TrueOrFalseFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return (
    <TrueOrFalseForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
